import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle, ChevronDown } from "lucide-react";
import { IMAGES, WA_DEFAULT, scrollToId } from "@/data/content";

const MaskedLine = ({ children, started, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={started ? { y: "0%" } : {}}
      transition={{ duration: 1.05, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function HeroSection({ started }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.04, 1.2]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src={IMAGES.hero}
          alt="Sleek red performance vehicle navigating a futuristic smart city skyline at night"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="hero-scrim absolute inset-0" />
      <div className="absolute inset-0 bg-gradient-to-t from-void via-transparent to-transparent" />
      <div className="scan-line absolute left-0 h-px w-full bg-gradient-to-r from-transparent via-brand/60 to-transparent" />

      <motion.div
        style={{ opacity: contentOpacity, y: contentY }}
        className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-24 pt-40 sm:px-6"
      >
        <motion.div
          initial={{ opacity: 0, x: -18 }}
          animate={started ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.35 }}
          className="mb-6 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-brand" />
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.35em] text-white/80 sm:text-xs">
            Global Investment Education & Executive Intelligence
          </span>
        </motion.div>

        <h1 className="font-display text-4xl font-black uppercase leading-[0.92] tracking-tight text-white sm:text-5xl lg:text-7xl">
          <MaskedLine started={started} delay={0.45}>
            Invest in <span className="text-brand">Knowledge.</span>
          </MaskedLine>
          <MaskedLine started={started} delay={0.62}>
            Explore the Future.
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={started ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.95 }}
          className="mt-6 max-w-xl text-sm leading-relaxed text-white/70 sm:text-base"
        >
          Discover educational insights into innovative industries, emerging
          technologies, and some of the world&rsquo;s most influential companies.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={started ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 1.1 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-primary-cta"
            onClick={() => scrollToId("showcase")}
            className="group inline-flex items-center gap-2.5 rounded-full bg-brand px-7 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-brand-crimson"
          >
            Get Started
            <ArrowRight
              size={15}
              className="transition-transform duration-300 group-hover:translate-x-1"
            />
          </button>
          <a
            data-testid="hero-secondary-cta"
            href={WA_DEFAULT}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 rounded-full border border-white/25 px-7 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.18em] text-white backdrop-blur-sm transition-colors duration-300 hover:border-wa hover:text-wa"
          >
            <MessageCircle size={15} />
            Contact Us on WhatsApp
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={started ? { opacity: 1 } : {}}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 font-mono text-[10px] uppercase tracking-[0.22em] text-white/45"
        >
          <span>40.7128° N / 74.0060° W</span>
          <span className="hidden sm:inline">Terminal v4.2.0</span>
          <span className="inline-flex items-center gap-2">
            <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-up" />
            Market Feed Active
          </span>
          <span className="max-w-xs normal-case tracking-normal text-white/35">
            Vehicle shown as a visual example of innovation. No affiliation with
            Tesla, Inc.
          </span>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("promo")}
        style={{ opacity: contentOpacity }}
        className="absolute bottom-7 left-1/2 z-10 -translate-x-1/2 text-white/50 transition-colors hover:text-brand"
        aria-label="Scroll down"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown size={26} />
        </motion.div>
      </motion.button>
    </section>
  );
}
